import React from 'react';
import { TemplateRenderProps } from './types';
import { FormFields, AddonsSelector, CouponSection, TrustAndSecurityBadge, SocialProofAndFomo } from './CheckoutCommonElements';
import { BillingCycleSelector, PriceBreakdown } from './CheckoutSummaryElements';

export const CleanStripeTemplate: React.FC<TemplateRenderProps> = ({
  plan,
  config,
  formData,
  onInputChange,
  billingCycle,
  onChangeCycle,
  selectedAddonIds,
  onToggleAddon,
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
  onSubmitInfo,
  isCapturing,
  currencySymbol,
  addonsTotal,
  discountAmount,
  finalTotal,
}) => {
  return (
    <div
      className="min-h-full p-4 md:p-8 flex items-center justify-center bg-[#f6f9fc] text-slate-800"
      style={{
        fontFamily: config.fontFamily || 'Inter, -apple-system, sans-serif',
      }}
    >
      <div className="w-full max-w-4xl bg-white rounded-xl shadow-lg border border-slate-200/80 overflow-hidden grid grid-cols-1 md:grid-cols-12 min-h-[560px]">
        {/* Left Stripe Style Summary */}
        <div className="md:col-span-5 p-6 md:p-8 bg-slate-50/70 border-b md:border-b-0 md:border-r border-slate-200/80 flex flex-col justify-between space-y-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-slate-400">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m15 18-6-6 6-6"/>
              </svg>
              <span className="text-xs font-semibold text-slate-600">Back</span>
            </div>

            {config.logoUrl && <img src={config.logoUrl} alt="Logo" className="h-6 object-contain" />}

            <div>
              <p className="text-xs text-slate-500 font-medium">Subscribe to</p>
              <h2 className="text-2xl font-bold text-slate-900">{plan.tier}</h2>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="text-3xl font-black text-slate-900">{currencySymbol}{finalTotal.toFixed(2)}</span>
                <span className="text-xs text-slate-500 font-medium">per {billingCycle.replace('ly', '')}</span>
              </div>
            </div>

            <BillingCycleSelector billingCycle={billingCycle} onChangeCycle={onChangeCycle} config={config} />
            <PriceBreakdown plan={plan} billingCycle={billingCycle} addonsTotal={addonsTotal} discountAmount={discountAmount} finalTotal={finalTotal} currencySymbol={currencySymbol} />
          </div>

          <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
            <span>Powered by</span>
            <span className="font-bold text-slate-600">Subscale</span>
          </div>
        </div>

        {/* Right Modern Form */}
        <div className="md:col-span-7 p-6 md:p-8 space-y-5 flex flex-col justify-between">
          <div className="space-y-4">
            <div>
              <h1 className="text-lg font-bold text-slate-900">{config.headerMain || 'Payment Information'}</h1>
              <p className="text-xs text-slate-500">{config.headerSub || 'Please provide your account details.'}</p>
            </div>

            <SocialProofAndFomo config={config} />

            <form onSubmit={onSubmitInfo} className="space-y-4">
              <FormFields
                config={config}
                formData={formData}
                onInputChange={onInputChange}
                inputClassName="bg-white border-slate-300 focus:border-[#635bff] focus:ring-2 focus:ring-[#635bff]/20 rounded-md"
              />
              <AddonsSelector addons={config.addons} selectedAddonIds={selectedAddonIds} onToggleAddon={onToggleAddon} currency={plan.currency} />
              <CouponSection config={config} appliedCoupon={appliedCoupon} onApplyCoupon={onApplyCoupon} onRemoveCoupon={onRemoveCoupon} />

              <button
                type="submit"
                disabled={isCapturing}
                className="w-full py-3.5 text-xs font-bold uppercase tracking-wider text-white shadow-sm transition-all hover:brightness-105 active:scale-[0.99] rounded-md disabled:opacity-50"
                style={{
                  backgroundColor: config.primaryColor || '#635bff',
                }}
              >
                {isCapturing ? 'Processing...' : config.buttonText || `Subscribe ${currencySymbol}${finalTotal.toFixed(2)}`}
              </button>
            </form>
          </div>

          <TrustAndSecurityBadge config={config} />
        </div>
      </div>
    </div>
  );
};
